/**
 * Live plumbing for the Clients LIST — a THIN SELECTOR over the shared realtime
 * bus (`src/realtime/`).
 *
 * The list needs a strict subset of what Client 360 needs: `clients` CRUD only,
 * and only rows that actually carry an id (it patches grid rows by id). That
 * subset used to justify a SECOND socket and a SECOND listener set for events
 * the Client 360 module was already receiving. It doesn't: the canonical
 * `clientCrudChannel` covers all four client-scoped services, and this module
 * SELECTS the `clients` rows out of it.
 *
 * CONNECTION: none is opened here — see `realtime/realtimeBus.ts`.
 * AUTH: sourced internally by the bus from the AuthStore; no token parameter.
 * IF THE STREAM IS DOWN: the bus records a loud diagnostic and the list stays
 * pull-only (header Refresh / post-write reloads) — see
 * `realtime/freshnessPolicy.ts`.
 */
import type RootStore from "stores/RootStore";
import { clientCrudChannel, subscribeRealtime, type CrudAction } from "realtime";
import type { ClientRecord } from "../clients.types";

export type ClientsLiveAction = CrudAction;

export interface ClientsLiveEvent {
  action: ClientsLiveAction;
  /** The full row the API published (Feathers sends the service result). */
  record: ClientRecord;
}

type ClientsLiveHandler = (event: ClientsLiveEvent) => void;

/**
 * Subscribe to live `clients` CRUD events. Returns an idempotent disposer.
 *
 * Selection rules (identical to the behaviour of the old dedicated listener):
 * only the `clients` service, and only records carrying an `id` — a payload
 * without one cannot be matched to a grid row.
 *
 * @param rootStore used by the bus to resolve the shared connection and the
 *   AuthStore JWT. The caller cannot supply a token.
 */
export function subscribeClientsEvents(
  rootStore: RootStore,
  handler: ClientsLiveHandler
): () => void {
  return subscribeRealtime(rootStore, clientCrudChannel, (event) => {
    if (event.service !== "clients") return;
    const record = event.record as unknown as ClientRecord;
    if (!record?.id) return;
    handler({ action: event.action, record });
  });
}
